import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect } from "react";
import {
  cleanPictures,
  enterPictureSection,
  exitPictureSection,
  loadPictures,
  postPicture,
  resetPictures
} from "redux/actions/pictureActions";
import { uploadedPicturesSelector } from "redux/selectors/picture";
import { dataSelector } from "redux/selectors/common";
import {
  maxNumberOfPicturesSelector,
  showCasePageSelector
} from "redux/selectors/config";
import { useLoadCaseOrMissionData } from "hooks/case-or-mission-data-loader-hooks";
import routes from "Routes/routesConfig";
import { useTranslate } from "hooks/translation-hooks";
import { getHistoryStart } from "redux/actions/historyActions";
import { event } from "analytics";
import { deleteEventStart } from "redux/actions/eventActions";
import { push } from "connected-react-router";
import { moveToNextStep, moveToPreviousStep } from "redux/actions/stepActions";
import {
  caseCreationEditModeSelector,
  savedPicturesSelector
} from "redux/selectors/caseCreation";
import {
  toggleCreateCaseIsEditMode,
  updateCaseCreation
} from "redux/actions/caseCreationActions";
import { useParams } from "react-router";
import { getCaseOrMissionPage } from "utils/app-utils";

const SUCCESS_MESSAGE = "upload_photos_card-success_message";
const ERROR_MESSAGE = "upload_photos_card-failure_message";
const PICTURES_UPLOAD_REQUIRED = "PICTURES_UPLOAD_REQUIRED";

const getNumberOfPictures = ({
  maximum_number_of_pictures,
  numberOfPicturesUploaded
}) => {
  const numberOfPictures =
    maximum_number_of_pictures - numberOfPicturesUploaded;
  return numberOfPictures >= 0 ? numberOfPictures : 0;
};

export const useUploadPicturesFollowUp = () => {
  useLoadCaseOrMissionData();
  const t = useTranslate();
  const dispatch = useDispatch();
  const pictures = useSelector(uploadedPicturesSelector);
  const maximum_number_of_pictures = useSelector(maxNumberOfPicturesSelector);
  const show_case_page = useSelector(showCasePageSelector);
  const { caseGuid, missionOrderId, numberOfPicturesUploaded } = useSelector(
    dataSelector
  );
  const enterThePictureSection = useCallback(() => {
    dispatch(enterPictureSection());
  }, [dispatch]);
  const exitThePictureSection = useCallback(() => {
    dispatch(exitPictureSection());
  }, [dispatch]);

  useEffect(() => {
    enterThePictureSection();
    return exitThePictureSection;
  }, [enterThePictureSection, exitThePictureSection]);

  const { id } = useParams();
  const linkPage = getCaseOrMissionPage({ id, showCasePage: show_case_page });

  useEffect(() => {
    if (caseGuid) {
      dispatch(getHistoryStart({ params: { caseGuid } }));
      dispatch(loadPictures(false));
    }
  }, [dispatch, caseGuid]);

  const removePictureEvent = () => {
    dispatch(
      deleteEventStart({
        type: PICTURES_UPLOAD_REQUIRED,
        missionOrderId
      })
    );
  };

  const sendPictures = () => {
    event("send", "photos_incident_uploaded");
    dispatch(cleanPictures());

    if (caseGuid) {
      dispatch(
        postPicture({
          pictures: pictures
            .filter(picture => picture.status === "LOADED")
            .slice(),
          caseGuid,
          missionOrderId,
          successMessage: t(SUCCESS_MESSAGE),
          errorMessage: t(ERROR_MESSAGE)
        })
      );
    }
  };

  const doneWithPictures = () => {
    if (!show_case_page) {
      removePictureEvent();
    }
    dispatch(push(linkPage));
  };

  const goToCurrentNotification = () => {
    const category = pictures.filter(({ status }) => status === "SENT").length
      ? "photos_incident_uploaded"
      : "take_photos_incident";
    event("back", category);
  };

  return {
    linkPage,
    sendPictures,
    doneWithPictures,
    goToCurrentNotification,
    numberOfPictures: getNumberOfPictures({
      maximum_number_of_pictures,
      numberOfPicturesUploaded
    }),
    pictures
  };
};

export const useUploadPicturesOpening = () => {
  const dispatch = useDispatch();
  const uploadedPictures = useSelector(uploadedPicturesSelector);
  const savedPictures = useSelector(savedPicturesSelector);
  const isEditMode = useSelector(caseCreationEditModeSelector);
  const maximum_number_of_pictures = useSelector(maxNumberOfPicturesSelector);
  const pictures = [...savedPictures, ...uploadedPictures];
  const doneWithPictures = () => {
    dispatch(
      updateCaseCreation({
        pictures: pictures.filter(({ status }) => status === "LOADED")
      })
    );
    dispatch(resetPictures());
    dispatch(moveToNextStep());
  };
  const numberOfPictures = maximum_number_of_pictures;
  const goBack = () => {
    event("back", "case_opening-incident_description");
    dispatch(isEditMode ? push(routes.SUMMARY) : moveToPreviousStep());
    if (isEditMode) dispatch(toggleCreateCaseIsEditMode());
  };

  return {
    doneWithPictures,
    goBack,
    numberOfPictures,
    pictures
  };
};
